/* eslint-disable @typescript-eslint/no-unused-vars */
// TODO: Fix unused vars

/**
 * Returns legend configuration with enhanced styling and functionality
 * @param paddingValue Legend padding value
 * @param onLegendClickHandler Custom legend click handler (optional)
 * @returns Legend configuration object
 */
export const getLegend = (
  paddingValue: number = 30,
  onLegendClickHandler?: (e: MouseEvent, legendItem: any) => boolean,
): any => {
  // Create the base legend configuration
  const legendConfig: any = {
    display: true,
    position: 'top' as const,
    align: 'start' as const,
    labels: {
      font: {
        size: 11,
      },
      usePointStyle: true,
      boxHeight: 10,
      boxWidth: 12,
      padding: 10,
      sort: (a: { text: string }, b: { text: string }) => {
        return a.text.localeCompare(b.text);
      },
    },
  };

  // Add custom click handler if provided
  if (onLegendClickHandler) {
    legendConfig.onClick = onLegendClickHandler;
  }

  return legendConfig;
};

/**
 * Default aspect ratio for charts
 * @returns Chart aspect ratio
 */
export const getAspectRatio = (): number => {
  return 3;
};

/**
 * Helper function to brighten or emphasize a color
 * @param color Color string (hex, rgb, rgba)
 * @param factor Factor to brighten by (1.0 means no change, 1.3 means 30% brighter)
 * @returns Modified color string with emphasis
 */
export const emphasizeColor = (color: string, factor: number = 1.3): string => {
  // Handle hex colors
  if (color.startsWith('#')) {
    // Convert hex to RGB
    let r = parseInt(color.slice(1, 3), 16);
    let g = parseInt(color.slice(3, 5), 16);
    let b = parseInt(color.slice(5, 7), 16);

    // Brighten values (cap at 255)
    r = Math.min(Math.round(r * factor), 255);
    g = Math.min(Math.round(g * factor), 255);
    b = Math.min(Math.round(b * factor), 255);

    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  // Handle rgb or rgba colors
  if (color.startsWith('rgb')) {
    const isRgba = color.startsWith('rgba');
    const values = color.match(/\d+(\.\d+)?/g);

    if (values && values.length >= 3) {
      const r = Math.min(Math.round(parseFloat(values[0]) * factor), 255);
      const g = Math.min(Math.round(parseFloat(values[1]) * factor), 255);
      const b = Math.min(Math.round(parseFloat(values[2]) * factor), 255);
      const a = isRgba && values.length > 3 ? parseFloat(values[3]) : 1;

      return isRgba ? `rgba(${r}, ${g}, ${b}, ${a})` : `rgb(${r}, ${g}, ${b})`;
    }
  }

  // If color format is unknown, return original
  return color;
};

/**
 * Adds transparency to a color
 * @param color Color string to modify
 * @param opacity Opacity value to apply (0-1)
 * @returns Color with transparency applied
 */
export const fadeColor = (color: string, opacity: number = 0.3): string => {
  if (!color) return 'rgba(0, 0, 0, ' + opacity + ')';

  // Handle hex colors
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // Handle rgb colors
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  }

  // Handle rgba colors - replace existing opacity
  if (color.startsWith('rgba(')) {
    return color.replace(/[\d.]+\)$/, `${opacity})`);
  }

  return color;
};
